import React from "react";
import { authorizedFetch } from "./boot.js";
import { Card, Tag } from "./ui.jsx";
import { LotIdentity } from "./lot_identity.jsx";

const { useEffect, useState } = React;

const clock = value => {
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? "" : at.toTimeString().slice(0, 5);
};

// Shaped on useParadeActivity in parade-activity.jsx, with two differences. It
// polls at ten seconds rather than thirty, because a horse is out and back
// inside ten minutes. And a failed refresh keeps the last good board and marks
// it stale instead of discarding it: a stale board still tells the second
// person what the first was doing, where an empty one would say nobody is
// waiting.
export function useParadeBoard(sale, vendor) {
  const [state, setState] = useState({ data: null, error: "", stale: false });
  const [attempt, retry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let timer, pending = false;
    const refresh = async () => {
      clearTimeout(timer);
      if (document.hidden || pending || controller.signal.aborted) return;
      pending = true;
      try {
        const query = new URLSearchParams({ sale, vendor });
        const response = await authorizedFetch(
          `/collections/vendor.inspections.parade@1/?${query}`, { signal: controller.signal });
        if (!response.ok) throw new Error(
          response.status === 404 ? "The parade board is not available for this draft, or your access has changed."
          : response.status === 401 ? "Sign in again to see the parade board."
          : "The parade board could not be refreshed.");
        const data = await response.json();
        if (!Array.isArray(data.cards)) throw new Error("The parade board could not be loaded.");
        if (!controller.signal.aborted) setState({ data, error: "", stale: false });
      } catch (error) {
        if (!controller.signal.aborted) setState(prev =>
          ({ ...prev, error: error.message, stale: Boolean(prev.data) }));
      } finally {
        pending = false;
        if (!controller.signal.aborted && !document.hidden) timer = setTimeout(refresh, 10000);
      }
    };
    const visibility = () => { clearTimeout(timer); if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", visibility);
    refresh();
    return () => {
      controller.abort(); clearTimeout(timer);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [sale, vendor, attempt]);
  return { ...state, refresh: () => retry(n => n + 1) };
}

async function write(path, input) {
  const response = await authorizedFetch(path, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ input, idempotency_key: window.crypto.randomUUID() }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const error = new Error(body?.error?.code || "request_failed");
    error.status = response.status;
    throw error;
  }
}

function ParadeSwitch({ sale, data, onChanged, say }) {
  const open = data.cards.length;
  const flip = async () => {
    // The confirmation names what will happen. The server refuses the write if
    // the count has moved since this was rendered, so a colleague opening a
    // card mid-confirmation cannot have it closed behind their back.
    if (data.enabled && open && !window.confirm(
      `Turning parade management off closes ${open} open card${open === 1 ? "" : "s"}. `
      + "Horses that were not seen stay marked unseen.")) return;
    try {
      await write("/actions/vendor.inspections.parade_preference@1/", {
        sale, enabled: !data.enabled, expected_revision: data.preferenceRevision,
        confirmed_open_cards: data.enabled ? open : 0,
      });
      say("");
    } catch (error) {
      // The path this guards is the whole D6 mechanism: the server correctly
      // refused a stale confirmed_open_cards (a colleague opened or closed a
      // card since the count was shown), and that refusal must be seen, not
      // swallowed into an unhandled rejection that leaves the Owner looking
      // at an unchanged board with no explanation.
      say(error.status === 412
        ? "The open cards changed — review the board and try again."
        : "That could not be saved. Try again.");
    } finally { onChanged(); }
  };
  return <button type="button" className="btn sm" data-parade-switch onClick={flip}>
    {data.enabled ? "Turn parade management off" : "Turn parade management on"}
  </button>;
}

function ParadeCard({ sale, card, canMark, onChanged, say }) {
  const [busy, setBusy] = useState("");
  const remaining = card.total - card.seenCount;
  const act = async (label, input, path) => {
    setBusy(label);
    try { await write(path, input); say(""); }
    catch (error) {
      say(error.status === 412
        ? "That changed while you were looking at it. The board has been reloaded."
        : "That could not be saved. Try again.");
    } finally { setBusy(""); onChanged(); }
  };
  const mark = (lot, seen) => act(lot.id, {
    sale, inspection_id: card.id, lot_id: lot.id, seen, expected_revision: lot.revision,
  }, "/actions/vendor.inspections.mark_seen@1/");
  return <Card title={card.buyer} subtitle={`${card.seenCount} of ${card.total} seen · arrived `
    + `${clock(card.openedAt)}${card.agent ? ` · ${card.agent}` : ""}`}>
    {card.note && <p className="caption">{card.note}</p>}
    <div className="parade-lots">
      {card.lots.map(lot => <div key={lot.id} className="parade-lot" data-parade-lot={lot.id}
        data-seen={lot.seen ? "1" : undefined}>
        <LotIdentity lot={lot}/>
        {lot.seen
          ? <span className="parade-seen">
              <Tag variant="gold">Seen</Tag>
              <small>{lot.seenByName || "Someone in your team"} · {clock(lot.seenAt)}</small>
              {canMark && <button type="button" className="btn sm" data-untick={lot.id}
                disabled={Boolean(busy)} onClick={() => mark(lot, false)}>Not yet</button>}
            </span>
          : canMark && <button type="button" className="btn sm primary" data-tick={lot.id}
              disabled={Boolean(busy)} onClick={() => mark(lot, true)}>Seen</button>}
      </div>)}
    </div>
    {canMark && <div className="row" style={{ justifyContent: "space-between", marginTop: 14 }}>
      <span className="muted" style={{ fontSize: 12 }}>
        {remaining ? `${remaining} still to be seen` : "Every horse has been seen"}
      </span>
      <button type="button" className="btn sm" data-close-card={card.id} disabled={Boolean(busy)}
        onClick={() => act("close", {
          sale, inspection_id: card.id, expected_revision: card.revision,
        }, "/actions/vendor.inspections.close_card@1/")}>
        {remaining ? `Close card · ${remaining} stay unseen` : "Close card"}
      </button>
    </div>}
    {canMark && remaining > 0 && <p className="caption">
      Horses left unseen were still inspected — unseen only means they were not walked out.
    </p>}
  </Card>;
}

export function ParadeManagement({ sale, vendor }) {
  const board = useParadeBoard(sale, vendor);
  const [notice, setNotice] = useState("");
  const data = board.data;
  // A failed read renders the failure. It never renders an empty board, which
  // would read as "nobody is waiting" and send staff home.
  if (!data) return <Card title="Parade management">
    {board.error
      ? <><p role="alert" className="error-text">{board.error}</p>
        <button type="button" className="btn" onClick={board.refresh}>Retry</button></>
      : <p role="status">Loading the board…</p>}
  </Card>;
  if (!data.enabled) return <Card title="Parade management"
    subtitle="This draft is not using the parade board">
    <p>Inspections are logged after the visit instead, exactly as before. Nothing else changes.</p>
    {notice && <p role="status" aria-live="polite">{notice}</p>}
    {data.canManage && <ParadeSwitch sale={sale} data={data} onChanged={board.refresh} say={setNotice}/>}
  </Card>;
  return <>
    <div className="activity-toolbar">
      <div><strong>Parade management</strong>
        <p>Who is waiting, and which horses they still have to see</p></div>
      {board.stale && <p role="status">Not refreshed just now — this is the last board.{" "}
        <button type="button" className="btn sm" onClick={board.refresh}>Retry</button></p>}
      {notice && <p role="status" aria-live="polite">{notice}</p>}
      {data.canManage && <ParadeSwitch sale={sale} data={data} onChanged={board.refresh} say={setNotice}/>}
    </div>
    {!data.cards.length && <Card title="Nobody is waiting">
      <p className="muted">Log an inspection when a buyer arrives and their horses appear here.</p>
    </Card>}
    {/* Server order is oldest arrival first: the buyer waiting longest is at
        the top. Do not re-sort here. */}
    {data.cards.map(card => <ParadeCard key={card.id} sale={sale} card={card}
      canMark={data.canMark} onChanged={board.refresh} say={setNotice}/>)}
  </>;
}
